import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  TextField,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Menu,
} from '@mui/material';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoAwesome as AutoAwesomeIcon,
  FileDownload as FileDownloadIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { apiClient } from '@/services/api';
import PerformanceIndicatorAIDialog from './PerformanceIndicatorAIDialog';
import PerformanceIndicatorSortDialog from './PerformanceIndicatorSortDialog';

// 固定绩效指标模板
const FIXED_PERFORMANCE_INDICATORS = [
  {
    "level1_name": "成本指标",
    "level2_name": "经济成本指标",
    "value_description": "经济成本是指花费金额，花钱必定有经济成本，定量指标，必填"
  },
  {
    "level1_name": "成本指标", 
    "level2_name": "社会成本指标",
    "value_description": "项目对社会产生负面影响，定量/定性指标，没有影响可不填"
  },
  {
    "level1_name": "成本指标",
    "level2_name": "生态环境成本指标", 
    "value_description": "项目对生态产生负面影响，定量/定性指标，没有影响可不填"
  },
  {
    "level1_name": "产出指标",
    "level2_name": "数量指标",
    "value_description": "定量指标，是对项目的产出结果的总结，必填"
  },
  {
    "level1_name": "产出指标",
    "level2_name": "质量指标", 
    "value_description": "定量指标，是对项目的产出结果的质量总结，必填"
  },
  {
    "level1_name": "产出指标",
    "level2_name": "时效指标",
    "value_description": "定量指标，是对项目的产出结果的时效总结，必填"
  },
  {
    "level1_name": "效益指标",
    "level2_name": "经济效益指标",
    "value_description": "项目对经济方面产生正面影响，定性/定量指标，没有影响可不填"
  },
  {
    "level1_name": "效益指标",
    "level2_name": "社会效益指标",
    "value_description": "项目对社会方面产生的正面影响，定性/定量指标，必填（项目产生和实施必定是对社会有正面影响的）"
  },
  {
    "level1_name": "效益指标",
    "level2_name": "生态效益指标", 
    "value_description": "对生态方面产生的正面影响，定性/定量指标，没有可不填（不涉及则完全不推荐，留空）"
  },
  {
    "level1_name": "满意度指标",
    "level2_name": "服务对象满意度指标",
    "value_description": "项目服务对象的满意程度，定量指标，必填"
  }
];

// 绩效指标数据类型定义
interface PerformanceIndicator {
  id: number;
  project_id: number;
  level1_code: string;
  level1_name: string;
  level2_code: string;
  level2_name: string;
  level3_code: string;
  level3_name: string;
  indicator_value: string;
  value_description: string;
  created_at: string;
  updated_at: string;
}

interface PerformanceIndicatorFormData {
  level1_code: string;
  level1_name: string;
  level2_code: string;
  level2_name: string;
  level3_code: string;
  level3_name: string;
  indicator_value: string;
  value_description: string;
}

const PerformanceIndicatorManager: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  
  // 状态管理
  const [indicators, setIndicators] = useState<PerformanceIndicator[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [project, setProject] = useState<any>(null);
  
  // 对话框状态
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [batchDeleteConfirmOpen, setBatchDeleteConfirmOpen] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState<PerformanceIndicator | null>(null);
  const [deletingIndicator, setDeletingIndicator] = useState<PerformanceIndicator | null>(null);
  
  // 批量删除相关状态
  const [selectedIndicatorIds, setSelectedIndicatorIds] = useState<number[]>([]);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);
  
  // 导出相关状态
  const [isExporting, setIsExporting] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  
  // 排序相关状态
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [isSorting, setIsSorting] = useState(false);

  // 项目绩效元信息（可编辑）
  const [meta, setMeta] = useState<any>({
    department_name: '',
    unit_name: '',
    annual_fund_total: '',
    project_funds_total: '',
    government_budget_fund: '',
    fiscal_special_account_fund: '',
    unit_fund: '',
    annual_target: '',
  });
  const [savingMeta, setSavingMeta] = useState(false);
  const [extractingMeta, setExtractingMeta] = useState(false);
  
  // AI推荐指标值相关状态
  const [recommendingValue, setRecommendingValue] = useState(false);
  
  // 表单数据
  const [formData, setFormData] = useState<PerformanceIndicatorFormData>({
    level1_code: '',
    level1_name: '',
    level2_code: '',
    level2_name: '',
    level3_code: '',
    level3_name: '',
    indicator_value: '',
    value_description: '',
  });

  // 获取项目信息
  const fetchProject = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      
      if (response.success && response.data) {
        setProject(response.data);
      } else {
        console.error('获取项目信息失败:', response.message);
      }
    } catch (err: any) {
      console.error('获取项目信息失败:', err);
    }
  }, [projectId]);

  // 获取项目绩效元信息
  const fetchProjectMeta = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await apiClient.get(`/projects/${projectId}/performance-meta`);
      if (response.success && response.data) {
        setMeta(response.data);
      }
    } catch (e) {
      console.error('获取项目元信息失败', e);
    }
  }, [projectId]);

  // 获取绩效指标列表
  const fetchIndicators = useCallback(async () => {
    if (!projectId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get(`/projects/${projectId}/performance-indicators?page=1&page_size=1000`);
      
      if (response.success && response.data) {
        setIndicators(response.data);
        setError(null);
      } else {
        setError(response.message || '获取绩效指标列表失败');
      }
    } catch (err: any) {
      console.error('获取绩效指标列表失败:', err);
      setError(err.response?.data?.detail || '获取绩效指标列表失败');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // 组件挂载时获取数据
  useEffect(() => {
    fetchProject();
    fetchProjectMeta();
    fetchIndicators();
  }, [fetchProject, fetchProjectMeta, fetchIndicators]);

  const handleSaveMeta = async () => {
    if (!projectId) return;
    setSavingMeta(true);
    try {
      const resp = await apiClient.put(`/projects/${projectId}/performance-meta`, meta);
      if (!resp.success) throw new Error(resp.message || '保存失败');
    } catch (e: any) {
      setError(e.message || '保存失败');
    } finally {
      setSavingMeta(false);
    }
  };

  const handleExtractMeta = async () => {
    if (!projectId) return;
    setExtractingMeta(true);
    try {
      const resp = await apiClient.post(`/projects/${projectId}/performance-meta/extract`, {});
      if (resp.success && resp.data) {
        // 优先使用 saved 数据（保存到数据库后的数据），向后兼容使用 extracted
        const dataToUpdate = resp.data.saved || resp.data.extracted;
        if (dataToUpdate) {
          // 只更新非空字段，空字段保留原有值
          const updatedMeta = { ...meta };
          Object.keys(dataToUpdate).forEach((key) => {
            if (dataToUpdate[key]) {  // 只更新非空值
              updatedMeta[key] = dataToUpdate[key];
            }
          });
          setMeta(updatedMeta);
          console.log('✅ 项目信息已更新:', updatedMeta);
        }
      } else if (!resp.success) {
        setError(resp.message || '抽取失败');
      }
    } catch (e: any) {
      setError(e.message || '抽取失败');
    } finally {
      setExtractingMeta(false);
    }
  };

  // AI推荐指标值
  const handleRecommendValue = async () => {
    if (!projectId || !formData.level3_name || !formData.level3_name.trim()) {
      setError('请先输入三级指标名称');
      return;
    }
    
    setRecommendingValue(true);
    try {
      const resp = await apiClient.post(
        `/projects/${projectId}/performance-indicators/ai-recommend-value`,
        { level3_name: formData.level3_name }
      );
      
      if (resp.success && resp.data && resp.data.indicator_value) {
        // 更新指标值
        setFormData({ ...formData, indicator_value: resp.data.indicator_value });
        console.log('✅ AI推荐指标值成功:', resp.data.indicator_value);
      } else {
        setError(resp.message || 'AI推荐失败');
      }
    } catch (e: any) {
      setError(e.message || 'AI推荐失败');
    } finally {
      setRecommendingValue(false);
    }
  };

  // 一级指标排序顺序
  const getLevel1Order = (level1Name: string) => {
    const order: Record<string, number> = {
      '成本指标': 1,
      '产出指标': 2,
      '效益指标': 3,
      '满意度指标': 4
    };
    return order[level1Name] || 999;
  };

  // 获取一级指标列表（去重）
  const level1Options = useMemo(() => {
    const level1Names = Array.from(new Set(FIXED_PERFORMANCE_INDICATORS.map(item => item.level1_name)));
    return level1Names.sort((a, b) => getLevel1Order(a) - getLevel1Order(b));
  }, []);

  // 根据一级指标获取二级指标列表
  const getLevel2Options = (level1Name: string) => {
    return FIXED_PERFORMANCE_INDICATORS
      .filter(item => item.level1_name === level1Name)
      .map(item => ({
        level2_name: item.level2_name,
        value_description: item.value_description
      }));
  };

  // 根据一级和二级指标获取指标说明
  const getValueDescription = (level1Name: string, level2Name: string) => {
    const item = FIXED_PERFORMANCE_INDICATORS.find(
      item => item.level1_name === level1Name && item.level2_name === level2Name
    );
    return item?.value_description || '';
  };

  // 获取排序后的指标列表
  const sortedIndicators = useMemo(() => {
    return [...indicators]
      // 先按三级编码排序（核心需求：按三级指标排序）
      .sort((a, b) => (a.level3_code || '').localeCompare(b.level3_code || ''))
      // 再保证一级指标的大类顺序不变（A/B/C/D 对应的展示顺序）
      .sort((a, b) => {
        const orderA = getLevel1Order(a.level1_name);
        const orderB = getLevel1Order(b.level1_name);
        return orderA - orderB;
      });
  }, [indicators]);


  // 处理一级指标选择
  const handleLevel1Change = (level1Name: string) => {
    setFormData({
      ...formData,
      level1_name: level1Name,
      level1_code: '', // 由后端生成
      level2_name: '', // 重置二级指标
      level2_code: '', // 由后端生成
      value_description: '', // 重置指标说明
    });
  };

  // 处理二级指标选择
  const handleLevel2Change = (level2Name: string) => {
    const description = getValueDescription(formData.level1_name, level2Name);
    setFormData({
      ...formData,
      level2_name: level2Name,
      level2_code: '', // 由后端生成
      value_description: description, // 自动填充指标说明
    });
  };

  // 处理添加指标
  const handleAddIndicator = async () => {
    if (!projectId) return;
    
    try {
      // 不传 code 字段，由后端自动生成
      const payload = {
        level1_name: formData.level1_name,
        level2_name: formData.level2_name,
        level3_name: formData.level3_name,
        indicator_value: formData.indicator_value,
        value_description: formData.value_description,
      };
      
      const response = await apiClient.post(`/projects/${projectId}/performance-indicators`, payload);
      
      if (response.success) {
        setAddDialogOpen(false);
        resetForm();
        fetchIndicators();
      } else {
        setError(response.message || '创建绩效指标失败');
      }
    } catch (err: any) {
      console.error('创建绩效指标失败:', err);
      setError(err.response?.data?.detail || '创建绩效指标失败');
    }
  };

  // 处理编辑指标
  const handleEditIndicator = async () => {
    if (!projectId || !editingIndicator) return;
    
    try {
      // 不传 code 字段，由后端自动生成
      const payload = {
        level1_name: formData.level1_name,
        level2_name: formData.level2_name,
        level3_name: formData.level3_name,
        indicator_value: formData.indicator_value,
        value_description: formData.value_description,
      };
      
      const response = await apiClient.put(
        `/projects/${projectId}/performance-indicators/${editingIndicator.id}`,
        payload
      );
      
      if (response.success) {
        setEditDialogOpen(false);
        setEditingIndicator(null);
        resetForm();
        fetchIndicators();
      } else {
        setError(response.message || '更新绩效指标失败');
      }
    } catch (err: any) {
      console.error('更新绩效指标失败:', err);
      setError(err.response?.data?.detail || '更新绩效指标失败');
    }
  };

  // 处理删除指标
  const handleDeleteIndicator = async () => {
    if (!projectId || !deletingIndicator) return;
    
    try {
      const response = await apiClient.delete(
        `/projects/${projectId}/performance-indicators/${deletingIndicator.id}`
      );
      
      if (response.success) {
        setDeleteDialogOpen(false);
        setDeletingIndicator(null);
        fetchIndicators();
      } else {
        setError(response.message || '删除绩效指标失败');
      }
    } catch (err: any) {
      console.error('删除绩效指标失败:', err);
      setError(err.response?.data?.detail || '删除绩效指标失败');
    }
  };

  // 重置表单
  const resetForm = () => {
    setFormData({
      level1_code: '',
      level1_name: '',
      level2_code: '',
      level2_name: '',
      level3_code: '',
      level3_name: '',
      indicator_value: '',
      value_description: '',
    });
  };

  // 打开编辑对话框
  const openEditDialog = (indicator: PerformanceIndicator) => {
    setEditingIndicator(indicator);
    setFormData({
      level1_code: indicator.level1_code,
      level1_name: indicator.level1_name,
      level2_code: indicator.level2_code,
      level2_name: indicator.level2_name,
      level3_code: indicator.level3_code,
      level3_name: indicator.level3_name,
      indicator_value: indicator.indicator_value,
      value_description: indicator.value_description,
    });
    setEditDialogOpen(true);
  };

  // 打开删除对话框
  const openDeleteDialog = (indicator: PerformanceIndicator) => {
    setDeletingIndicator(indicator);
    setDeleteDialogOpen(true);
  };

  // 处理AI推荐完成
  const handleAIRecommendationComplete = async (result: any) => {
    if (!projectId || !result?.data?.recommendedIndicators) return;
    
    try {
      // 批量创建推荐的绩效指标，code由后端自动生成
      const indicatorsToCreate = result.data.recommendedIndicators.map((indicator: any) => ({
        level1_code: '', // 由后端生成
        level1_name: indicator.level1_name || '',
        level2_code: '', // 由后端生成
        level2_name: indicator.level2_name || '',
        level3_code: '', // 由后端生成
        level3_name: indicator.level3_name || '',
        indicator_value: String(indicator.indicator_value || ''),
        value_description: indicator.value_description || '',
      }));

      // 使用批量创建接口
      const response = await apiClient.post(`/projects/${projectId}/performance-indicators/batch`, {
        indicators: indicatorsToCreate
      });

      if (response.success) {
        // 刷新指标列表
        fetchIndicators();
        console.log(`成功批量创建 ${indicatorsToCreate.length} 个绩效指标`);
      } else {
        setError(response.message || '批量创建绩效指标失败');
      }
    } catch (err: any) {
      console.error('批量创建绩效指标失败:', err);
      setError(err.response?.data?.detail || '批量创建绩效指标失败');
    }
  };

  // 打开排序对话框
  const handleOpenSortDialog = () => {
    setSortDialogOpen(true);
  };

  // 关闭排序对话框
  const handleCloseSortDialog = () => {
    setSortDialogOpen(false);
  };

  // 保存排序
  const handleSaveSort = async (newCodes: any[]) => {
    setIsSorting(true);
    try {
      const response = await apiClient.post(`/projects/${projectId}/performance-indicators/reorder`, {
        newCodes
      });

      if (response.success) {
        setSortDialogOpen(false);
        // 刷新指标列表
        fetchIndicators();
      } else {
        setError(response.message || '排序保存失败');
      }
    } catch (err: any) {
      console.error('排序保存失败:', err);
      setError(err.response?.data?.detail || '排序保存失败');
    } finally {
      setIsSorting(false);
    }
  };

  // 批量删除相关函数
  const handleSelectIndicatorForBatchDelete = (indicatorId: number, selected: boolean) => {
    setSelectedIndicatorIds(prev => {
      if (selected) {
        return [...prev, indicatorId];
      } else {
        return prev.filter(id => id !== indicatorId);
      }
    });
  };

  const handleSelectAllIndicatorsForBatchDelete = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedIndicatorIds(indicators.map(ind => ind.id));
    } else {
      setSelectedIndicatorIds([]);
    }
  };

  const handleOpenBatchDeleteDialog = () => {
    if (selectedIndicatorIds.length === 0) {
      setError('请先选择要删除的绩效指标');
      return;
    }
    setBatchDeleteConfirmOpen(true);
  };

  const handleCloseBatchDeleteDialog = () => {
    setBatchDeleteConfirmOpen(false);
  };

  // 导出Excel
  const handleExportExcel = async () => {
    if (!projectId) return;
    setIsExporting(true);
    try {
      const resp = await fetch(`/api/v1/projects/${projectId}/performance-indicators/export-excel`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        }
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || '导出失败');
      }
      
      // 从响应头中提取文件名
      const contentDisposition = resp.headers.get('Content-Disposition') || '';
      let filename = `项目_${projectId}_绩效目标申报表.xlsx`; // 默认文件名
      
      // 优先使用后端返回的文件名
      const filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
      if (filenameMatch) {
        filename = decodeURIComponent(filenameMatch[1]);
      } else {
        // 如果没有找到，尝试其他格式
        const altMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (altMatch) {
          filename = altMatch[1];
        }
      }
      
      // 如果项目信息已获取，使用项目名称生成文件名
      if (project && project.project_name) {
        const safeName = project.project_name.replace(/[\/\\:*?"<>|]/g, '_');
        filename = `${safeName}_绩效目标申报表.xlsx`;
      }
      
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      setError(error.message || '导出失败');
    } finally {
      setIsExporting(false);
    }
  };

  // 导出Word
  const handleExportWord = async () => {
    if (!projectId) return;
    setIsExporting(true);
    try {
      const resp = await fetch(`/api/v1/projects/${projectId}/performance-indicators/export-word`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        }
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || '导出失败');
      }
      
      // 从响应头中提取文件名
      const contentDisposition = resp.headers.get('Content-Disposition') || '';
      let filename = `项目_${projectId}_绩效目标申报表.docx`; // 默认文件名
      
      // 优先使用后端返回的文件名
      const filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
      if (filenameMatch) {
        filename = decodeURIComponent(filenameMatch[1]);
      } else {
        // 如果没有找到，尝试其他格式
        const altMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (altMatch) {
          filename = altMatch[1];
        }
      }
      
      // 如果项目信息已获取，使用项目名称生成文件名
      if (project && project.project_name) {
        const safeName = project.project_name.replace(/[\/\\:*?"<>|]/g, '_');
        filename = `${safeName}_绩效目标申报表.docx`;
      }
      
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      setError(error.message || '导出失败');
    } finally {
      setIsExporting(false);
    }
  };

  // 打开导出菜单
  const openExportMenu = (e: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(e.currentTarget);
  };

  // 关闭导出菜单
  const closeExportMenu = () => {
    setExportMenuAnchor(null);
  };

  const handleConfirmBatchDelete = async () => {
    if (selectedIndicatorIds.length === 0) return;
    
    setIsBatchDeleting(true);
    setError(null);
    
    try {
      const response = await apiClient.post(`/projects/${projectId}/performance-indicators/batch-delete`, {
        indicator_ids: selectedIndicatorIds
      });

      if (response.success) {
        setSelectedIndicatorIds([]);
        fetchIndicators(); // 刷新列表
        setBatchDeleteConfirmOpen(false);
      } else {
        throw new Error(response.error || '批量删除失败');
      }
    } catch (err: any) {
      console.error("Error batch deleting performance indicators:", err);
      setError(err.message || '批量删除失败');
    } finally {
      setIsBatchDeleting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        {/* 标题和操作栏 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            绩效指标管理
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setAddDialogOpen(true)}
            >
              添加指标
            </Button>
            {indicators.length === 0 && (
              <Button
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setAiDialogOpen(true)}
              >
                AI推荐指标
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<TuneIcon />}
              onClick={handleOpenSortDialog}
              disabled={isSorting}
            >
              指标排序
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={openExportMenu}
              disabled={isExporting}
            >
              导出
            </Button>
            <Menu anchorEl={exportMenuAnchor} open={Boolean(exportMenuAnchor)} onClose={closeExportMenu}>
              <MenuItem onClick={() => { closeExportMenu(); handleExportExcel(); }} disabled={isExporting}>
                导出为 Excel (.xlsx)
              </MenuItem>
              <MenuItem onClick={() => { closeExportMenu(); handleExportWord(); }} disabled={isExporting}>
                导出为 Word (.docx)
              </MenuItem>
            </Menu>
          {selectedIndicatorIds.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleOpenBatchDeleteDialog}
            >
              批量删除 ({selectedIndicatorIds.length})
            </Button>
          )}
          </Stack>
        </Box>


        {/* 错误提示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* 加载状态 */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {/* 顶部：项目绩效元信息（可编辑） */}
        <Paper sx={{ p: 1.5, mb: 2 }} variant="outlined">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>项目信息</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" onClick={handleExtractMeta} disabled={extractingMeta}>
                {extractingMeta ? '提取中...' : '提取项目信息'}
              </Button>
              <Button size="small" variant="contained" onClick={handleSaveMeta} disabled={savingMeta}>
                {savingMeta ? '保存中...' : '保存项目信息'}
              </Button>
            </Box>
          </Box>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="部门名称" fullWidth size="small" value={meta.department_name}
                onChange={(e) => setMeta({ ...meta, department_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="单位名称" fullWidth size="small" value={meta.unit_name}
                onChange={(e) => setMeta({ ...meta, unit_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="年度资金总额" fullWidth size="small" value={meta.annual_fund_total}
                onChange={(e) => setMeta({ ...meta, annual_fund_total: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="项目资金总计" fullWidth size="small" value={meta.project_funds_total}
                onChange={(e) => setMeta({ ...meta, project_funds_total: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="政府预算资金" fullWidth size="small" value={meta.government_budget_fund}
                onChange={(e) => setMeta({ ...meta, government_budget_fund: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="财政专户管理资金" fullWidth size="small" value={meta.fiscal_special_account_fund}
                onChange={(e) => setMeta({ ...meta, fiscal_special_account_fund: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="单位资金" fullWidth size="small" value={meta.unit_fund}
                onChange={(e) => setMeta({ ...meta, unit_fund: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="年度目标" fullWidth multiline rows={2} size="small" value={meta.annual_target}
                onChange={(e) => setMeta({ ...meta, annual_target: e.target.value })} />
            </Grid>
            
          </Grid>
        </Paper>

        {/* 指标表格 */}
        {!loading && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ backgroundColor: 'action.hover' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '3%' }} padding="checkbox">
                    <Checkbox
                      checked={selectedIndicatorIds.length === indicators.length && indicators.length > 0}
                      indeterminate={selectedIndicatorIds.length > 0 && selectedIndicatorIds.length < indicators.length}
                      onChange={(e) => handleSelectAllIndicatorsForBatchDelete(e.target.checked)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '2%' }}>序号</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>一级指标</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>二级指标</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>三级指标</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>指标值</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>指标值说明</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '7%', textAlign: 'right' }}>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedIndicators.map((indicator, index) => (
                  <TableRow key={indicator.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIndicatorIds.includes(indicator.id)}
                        onChange={(e) => handleSelectIndicatorForBatchDelete(indicator.id, e.target.checked)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      {indicator.level1_name}
                    </TableCell>
                    <TableCell>
                      {indicator.level2_name}
                    </TableCell>
                    <TableCell>
                      {indicator.level3_name || ''}
                    </TableCell>
                    <TableCell>{indicator.indicator_value}</TableCell>
                    <TableCell>{indicator.value_description}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="编辑">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(indicator)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="删除">
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(indicator)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* 空状态 */}
        {!loading && indicators.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              暂无绩效指标数据
            </Typography>
          </Box>
        )}
      </Paper>

      {/* 添加指标对话框 */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>添加绩效指标</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>一级指标</InputLabel>
                <Select
                  value={formData.level1_name}
                  label="一级指标"
                  onChange={(e) => handleLevel1Change(e.target.value)}
                >
                  {level1Options.map((level1Name) => (
                    <MenuItem key={level1Name} value={level1Name}>
                      {level1Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>二级指标</InputLabel>
                <Select
                  value={formData.level2_name}
                  label="二级指标"
                  onChange={(e) => handleLevel2Change(e.target.value)}
                  disabled={!formData.level1_name}
                >
                  {getLevel2Options(formData.level1_name).map((option) => (
                    <MenuItem key={option.level2_name} value={option.level2_name}>
                      {option.level2_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="三级指标"
                value={formData.level3_name}
                onChange={(e) => setFormData({ ...formData, level3_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="指标值"
                  value={formData.indicator_value}
                  onChange={(e) => setFormData({ ...formData, indicator_value: e.target.value })}
                />
                <Button
                  variant="outlined"
                  onClick={handleRecommendValue}
                  disabled={!formData.level3_name || !formData.level3_name.trim() || recommendingValue}
                  sx={{ minWidth: '140px', height: '56px' }}
                >
                  {recommendingValue ? '推荐中...' : 'AI推荐指标值'}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="指标值说明"
                multiline
                rows={3}
                value={formData.value_description}
                onChange={(e) => setFormData({ ...formData, value_description: e.target.value })}
                disabled
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>取消</Button>
          <Button onClick={handleAddIndicator} variant="contained">确定</Button>
        </DialogActions>
      </Dialog>

      {/* 编辑指标对话框 */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>编辑绩效指标</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>一级指标</InputLabel>
                <Select
                  value={formData.level1_name}
                  label="一级指标"
                  onChange={(e) => handleLevel1Change(e.target.value)}
                >
                  {level1Options.map((level1Name) => (
                    <MenuItem key={level1Name} value={level1Name}>
                      {level1Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>二级指标</InputLabel>
                <Select
                  value={formData.level2_name}
                  label="二级指标"
                  onChange={(e) => handleLevel2Change(e.target.value)}
                  disabled={!formData.level1_name}
                >
                  {getLevel2Options(formData.level1_name).map((option) => (
                    <MenuItem key={option.level2_name} value={option.level2_name}>
                      {option.level2_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="三级指标"
                value={formData.level3_name}
                onChange={(e) => setFormData({ ...formData, level3_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="指标值"
                value={formData.indicator_value}
                onChange={(e) => setFormData({ ...formData, indicator_value: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="指标值说明"
                multiline
                rows={3}
                value={formData.value_description}
                onChange={(e) => setFormData({ ...formData, value_description: e.target.value })}
                disabled
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>取消</Button>
          <Button onClick={handleEditIndicator} variant="contained">确定</Button>
        </DialogActions>
      </Dialog>

      {/* 删除确认对话框 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            确定要删除这个绩效指标吗？此操作不可撤销。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
          <Button onClick={handleDeleteIndicator} variant="contained" color="error">
            删除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 批量删除确认对话框 */}
      <Dialog
        open={batchDeleteConfirmOpen}
        onClose={handleCloseBatchDeleteDialog}
        aria-labelledby="batch-delete-dialog-title"
        aria-describedby="batch-delete-dialog-description"
      >
        <DialogTitle id="batch-delete-dialog-title">
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">确认批量删除绩效指标</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="batch-delete-dialog-description">
            <Typography variant="body1" gutterBottom>
              此操作不可恢复，确定要删除选中的 {selectedIndicatorIds.length} 个绩效指标吗？
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              选中的绩效指标：
            </Typography>
            <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
              {indicators
                .filter(ind => selectedIndicatorIds.includes(ind.id))
                .map((indicator, index) => (
                  <Typography key={indicator.id} variant="body2" sx={{ py: 0.5 }}>
                    {index + 1}. {indicator.level3_name}
                  </Typography>
                ))}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBatchDeleteDialog} disabled={isBatchDeleting}>
            取消
          </Button>
          <Button 
            onClick={handleConfirmBatchDelete} 
            variant="contained" 
            color="error"
            disabled={isBatchDeleting}
            startIcon={isBatchDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {isBatchDeleting ? '删除中...' : '确认删除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI推荐对话框 */}
      <PerformanceIndicatorAIDialog
        open={aiDialogOpen}
        onClose={() => setAiDialogOpen(false)}
        project={project ? {
          id: project.id.toString(),
          project_name: project.project_name || '',
          industry_type: project.industry_type || '',
          client_level: project.client_level || '',
          evaluation_type: project.evaluation_type || '',
          description: project.description || ''
        } : null}
        onRecommendationComplete={handleAIRecommendationComplete}
      />

      {/* 排序对话框 */}
      <PerformanceIndicatorSortDialog
        open={sortDialogOpen}
        onClose={handleCloseSortDialog}
        indicators={indicators}
        onSave={handleSaveSort}
        isSaving={isSorting}
      />
    </Box>
  );
};

export default PerformanceIndicatorManager;